import React from "react";
import { motion } from "framer-motion";
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { useWallet } from "@/context/WalletContext";
import { usePrice, usePolling } from "@/components/croc/hooks";
import { StatCard, Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtUsd, fmtNum, fmtCompact, pct } from "@/lib/format";
import api from "@/lib/api";
import { CHAIN } from "@/contracts/croc";
import { RankShareCard } from "@/components/croc/RankShareCard";
import { LiquidityPoolCard } from "@/components/croc/LiquidityPoolCard";
import { ExchangePanel } from "@/components/croc/ExchangePanel";
import { PayoutCountdown } from "@/components/croc/PayoutCountdown";
import { RestakeNotice } from "@/components/croc/RestakeNotice";
import { CapAlert } from "@/components/croc/CapAlert";
import { RankNudge } from "@/components/croc/RankNudge";
import { RewardsHistory } from "@/components/croc/RewardsHistory";
import { PackageCards } from "@/components/croc/PackageCards";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Lock, Flame, Users, Layers, Droplets, Activity, ArrowUpRight, Gauge, ShieldCheck, Radio, ExternalLink, Coins, Copy, Link2, Share2, QrCode, Check, Network,
} from "lucide-react";

const EVENT_META = {
  Staked: "New stake", Registered: "Referral joined", VolumeCredited: "Volume credited",
  SponsorBonus: "Sponsor bonus", RankUpdated: "Rank updated", RankRewardPaid: "Rank reward",
  PoolContribution: "Pool funded", PoolDistributed: "Pool payout", RoundOpened: "Prediction round opened",
  BetPlaced: "Prediction bet", RoundSettled: "Round settled", WithdrawalRecorded: "Withdrawal",
  LevelIncomePaid: "Override income", SharesAllocated: "Shares allocated", ShareValueFunded: "Share pool funded",
  PriceUpdated: "Oracle price push",
};

function shortA(a) {
  return a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}
function eventDetail(args) {
  if (!args) return "";
  const addr = args.user || args.recipient || args.sponsor || args.winners?.[0];
  return typeof addr === "string" && addr.startsWith("0x") ? shortA(addr) : "";
}

export default function Overview() {
  const { user, address, networkName } = useWallet();
  const price = usePrice();
  const { data: metrics } = usePolling(() => api.metrics(), [], 20000);
  const { data: history } = usePolling(() => api.priceHistory(), [], 60000);
  const { data: feed } = usePolling(() => api.chainEvents({ limit: 14 }), [], 15000);
  const up = (price?.change_24h ?? 0) >= 0;
  const code = user?.referral_code || "";
  const refLink = code ? `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${code}` : "";

  return (
    <div>
      <SectionTitle
        eyebrow="Protocol Master Command Center"
        title="Overview"
        subtitle="Live protocol telemetry — TVL, yield, supply and reward flow, all revenue-backed."
        right={
          <div className="flex items-center gap-2">
            {metrics?.source === "chain" && (
              <Pill tone="gold" data-testid="metrics-chain-badge"><Radio className="w-3 h-3" /> Chain-backed</Pill>
            )}
            <Pill tone="emerald"><ShieldCheck className="w-3 h-3" /> Revenue-Backed</Pill>
          </div>
        }
        testid="overview-title"
      />

      <RankNudge />

      <RestakeNotice user={user} />
      <CapAlert user={user} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Value Locked" value={fmtUsd(metrics?.tvl)} sub={`${metrics?.active_stakes ?? 0} active stakes`} icon={Lock} accent="emerald" testid="stat-tvl" delay={0} />
        <StatCard label="Rewards Distributed" value={fmtUsd(metrics?.rewards_distributed)} sub="from revenue pool" icon={Activity} accent="gold" testid="stat-rewards" delay={0.05} />
        <StatCard label="CROC Burned" value={fmtCompact(metrics?.burned)} sub="deflationary on sell" icon={Flame} accent="gold" testid="stat-burned" delay={0.1} />
        <StatCard label="Protocol Users" value={fmtNum(metrics?.users, 0)} sub={`${fmtUsd(metrics?.turnover)} turnover`} icon={Users} accent="emerald" testid="stat-users" delay={0.15} />
      </div>

      {address && (
        <div className="croc-panel p-5 mb-6 flex items-center justify-between flex-wrap gap-4" data-testid="locked-croc-card">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#1CFD4C]/12 border border-[#1CFD4C]/30 grid place-items-center shrink-0">
              <Lock className="w-5 h-5 emerald-text" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">CROC Locked in Contract</div>
              <div className="font-num font-black text-2xl text-white">
                {fmtNum(user?.locked_croc, 2)} <span className="text-sm emerald-text">CROC</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-num text-lg gold-text">{fmtUsd((user?.locked_croc ?? 0) * (price?.price ?? 0))}</div>
            <div className="text-[10px] text-muted-foreground">non-transferable until each stake matures</div>
          </div>
        </div>
      )}

      {/* referral link & share banner on dashboard */}
      <Panel glow="gold" className="mb-6" data-testid="overview-referral-banner">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 grid place-items-center shrink-0">
              <Share2 className="w-5 h-5 gold-text" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-white text-base">Your Referral Link</span>
                <Pill tone="gold" className="text-[10px]">5% Instant Bonus</Pill>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Share your link to earn 5% instant bonus on every direct stake + unlock 21 levels of override income.
              </p>
            </div>
          </div>

          {code ? (
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              <div className="flex items-center gap-2 bg-[#090E0B] border border-white/10 rounded-xl px-3 py-2 text-xs font-num text-muted-foreground truncate max-w-full sm:max-w-xs md:max-w-sm">
                <Link2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="truncate text-white">{refLink}</span>
              </div>
              <button
                data-testid="overview-referral-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(refLink);
                  toast.success("Referral link copied to clipboard!");
                }}
                className="flex items-center gap-1.5 rounded-xl bg-[#1CFD4C] px-3.5 py-2 text-xs font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors shrink-0"
              >
                <Copy className="w-3.5 h-3.5" /> Copy Link
              </button>
              <div className="flex items-center gap-1.5 bg-[#090E0B] border border-white/10 rounded-xl px-2.5 py-2 text-xs shrink-0">
                <span className="text-[10px] uppercase text-muted-foreground">Code:</span>
                <span className="font-num font-bold gold-text">{code}</span>
                <button
                  data-testid="overview-referral-code-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(code);
                    toast.success("Referral code copied!");
                  }}
                  className="text-muted-foreground hover:text-white ml-1"
                  title="Copy code only"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
              <Link
                to="/referrals"
                className="flex items-center gap-1 text-xs gold-text hover:underline px-2 py-2 shrink-0"
                data-testid="overview-referral-hub-link"
              >
                Referral Hub →
              </Link>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">
              Connect wallet to access your referral link.
            </div>
          )}
        </div>
      </Panel>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* price chart */}
        <Panel className="lg:col-span-3 min-w-0" data-testid="overview-price-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs text-muted-foreground">CROC / USDT · Oracle Feed</div>
              <div className="flex items-baseline gap-3">
                <span className="font-num font-black text-3xl text-white">${fmtNum(price?.price, 4)}</span>
                <span className={`font-num ${up ? "tick-up" : "tick-down"}`}>{fmtNum(price?.change_24h, 2)}%</span>
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground font-num">
              <div>H ${fmtNum(price?.high_24h, 4)}</div>
              <div>L ${fmtNum(price?.low_24h, 4)}</div>
            </div>
          </div>
          <div className="h-56 w-full min-w-0">
            <ResponsiveContainer width="99%" height={224}>
              <AreaChart data={history?.points ?? []}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={["dataMin", "dataMax"]} hide />
                <Tooltip
                  contentStyle={{ background: "#0F1712", border: "1px solid rgba(229,72,77,0.3)", borderRadius: 12, color: "#fff" }}
                  labelFormatter={() => ""}
                  formatter={(v) => [`$${Number(v).toFixed(4)}`, "CROC"]}
                />
                <Area type="monotone" dataKey="p" stroke="#34D399" strokeWidth={2.5} fill="url(#g)" isAnimationActive={false} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">
            Reference: {price?.reference?.asset} ${fmtNum(price?.reference?.price, 2)} ({fmtNum(price?.reference?.change_24h, 2)}%) · live via Coinbase oracle
          </div>
        </Panel>
      </div>

      {/* my position + quick actions */}
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Panel className="lg:col-span-2" data-testid="overview-position">
          <div className="flex items-center justify-between mb-4">
            <span className="font-display font-bold text-lg text-white">Your Position</span>
            <Pill tone="gold">{user?.rank?.name ?? "Hatchling"}</Pill>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Metric label="Active Staked" value={fmtUsd(user?.active_staked)} />
            <Metric label="Accuring Today" value={fmtUsd(user?.pending_roi)} sub={user?.projected_daily_roi ? `≈ ${fmtUsd(user.projected_daily_roi)}/day` : undefined} accent />
            <Metric label="Withdrawable" value={fmtUsd(user?.claimable)} />
            <Metric label="Team Volume" value={fmtUsd(user?.team_volume)} />
          </div>
          <div className="mt-4"><PayoutCountdown /></div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to="/buy" data-testid="quick-buy-link" className="flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors">
              <Layers className="w-4 h-4" /> Buy & Stake
            </Link>
            <Link to="/withdraw" data-testid="quick-withdraw-link" className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-white hover:border-[#1CFD4C]/50 transition-colors">
              Withdraw <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Panel>

        <Panel data-testid="overview-pools">
          <div className="flex items-center gap-2 mb-4">
            <Droplets className="w-4 h-4 emerald-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Reward Pools</span>
          </div>
          <PoolRow label="Croc Pool" value={fmtUsd(metrics?.croc_pool)} />
          <PoolRow label="Share Pool" value={fmtUsd(metrics?.share_pool)} />
          <Link to="/pools" className="mt-3 block text-center text-sm gold-text hover:underline" data-testid="overview-pools-link">
            View leaderboards →
          </Link>
        </Panel>
      </div>

      {/* my income & stake breakdown */}
      <Panel className="mt-6" data-testid="income-breakdown">
        <div className="flex items-center gap-2 mb-4">
          <Coins className="w-4 h-4 gold-text" />
          <span className="font-display font-bold text-lg text-white">My Income & Stake</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Metric label="Total Staked" value={fmtUsd(user?.total_staked)} testid="income-total-staked" />
          <Metric label="Tokens Staked" value={`${fmtNum(user?.total_token_staked, 2)} CROC`} testid="income-tokens-staked" />
          <Metric label="ROI Income" value={fmtUsd(user?.income?.roi)} accent testid="income-roi" />
          <Metric label="Direct Income" value={fmtUsd(user?.income?.direct)} accent testid="income-direct" />
          <Metric label="Override Income" value={fmtUsd(user?.income?.override)} accent testid="income-override" />
          <Metric label="Rank Income" value={fmtUsd(user?.income?.rank)} accent testid="income-rank" />
          <Metric label="Prediction Bonus" value={fmtUsd(user?.income?.prediction)} accent testid="income-prediction" />
          <Metric label="Share Income" value={fmtUsd(user?.income?.share)} accent testid="income-share" />
          <Metric label="Croc Pool Income" value={fmtUsd(user?.income?.croc_pool)} accent testid="income-croc-pool" />
          <Metric label="Total Income" value={fmtUsd(user?.income?.total)} accent testid="income-total" />
        </div>
      </Panel>

      <LiquidityPoolCard />

      <div className="mb-6" data-testid="overview-sell-section">
        <ExchangePanel />
      </div>

      <RankShareCard />

      {/* my packages dashboard */}
      <PackageCards />

      {/* team legs: power vs weaker business */}
      <Panel className="mt-6" data-testid="team-legs">
        <div className="flex items-center gap-2 mb-4">
          <Network className="w-4 h-4 gold-text" />
          <span className="font-display font-bold text-lg text-white">Team Legs · Power vs Weaker Business</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-[#1CFD4C]/25 bg-[#1CFD4C]/[0.05] p-4" data-testid="leg-power">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Power Leg (Strongest)</div>
            <div className="font-num font-black text-2xl emerald-text mt-1">{fmtUsd(user?.legs?.power)}</div>
            {user?.legs?.power_address && (
              <div className="text-[10px] text-muted-foreground font-num mt-1 break-all">{user.legs.power_address}</div>
            )}
          </div>
          <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] p-4" data-testid="leg-weak">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Weaker Legs (Matching)</div>
            <div className="font-num font-black text-2xl gold-text mt-1">{fmtUsd(user?.legs?.weak)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">counts toward rank matching</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4" data-testid="leg-total">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Leg Business</div>
            <div className="font-num font-black text-2xl text-white mt-1">{fmtUsd(user?.legs?.total)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">{user?.legs?.count ?? 0} direct legs</div>
          </div>
        </div>
        {(user?.legs?.breakdown?.length ?? 0) > 0 && (
          <div className="mt-4 space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
            {user.legs.breakdown.map((l, i) => (
              <div key={l.address} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-2.5" data-testid={`leg-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Pill tone={i === 0 ? "emerald" : "gold"} className="text-[9px] shrink-0">{i === 0 ? "POWER" : "WEAK"}</Pill>
                  <span className="font-num text-xs text-white break-all">{l.address}</span>
                </div>
                <span className="font-num text-sm emerald-text shrink-0 ml-2">{fmtUsd(l.volume)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* live on-chain activity */}
      <Panel className="mt-6" data-testid="overview-activity-feed">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 emerald-text" />
            <span className="font-display font-bold text-white">On-Chain Activity</span>
          </div>
          <Pill tone="emerald"><Radio className="w-3 h-3" /> Live · {networkName || "BSC Mainnet"}</Pill>
        </div>
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
          {(feed?.events ?? []).map((e, i) => (
            <a
              key={`${e.tx}-${i}`}
              href={`${CHAIN.explorer}/tx/${e.tx}`}
              target="_blank" rel="noreferrer"
              className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-2.5 hover:border-[#1CFD4C]/40 transition-colors group"
              data-testid={`activity-row-${i}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-2 h-2 rounded-full bg-[#34D399] shrink-0 animate-pulse" />
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{EVENT_META[e.event] || e.event}</div>
                  <div className="text-[10px] text-muted-foreground font-num">{e.contract} · block {e.block}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] font-num text-muted-foreground">{eventDetail(e.args)}</span>
                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
          {(feed?.events?.length ?? 0) === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">Waiting for on-chain activity…</div>
          )}
        </div>
      </Panel>

      <div className="mt-6"><RewardsHistory limit={8} title="Recent Rewards" /></div>
    </div>
  );
}

function Metric({ label, value, sub, accent, testid }) {  return (
    <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-num font-bold text-lg mt-1 ${accent ? "emerald-text" : "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
function PoolRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-num font-bold text-white">{value}</span>
    </div>
  );
}
